import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Plus, Pencil, Trash2 } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminSection } from "@/components/admin/AdminSection";
import { MediaField } from "@/components/admin/MediaField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { listSeoPages, saveSeoPage, deleteSeoPage } from "@/lib/admin/cms.functions";

export const Route = createFileRoute("/_admin/admin/cms/seo")({
  component: SeoAdminPage,
});

type SeoRow = {
  id: string;
  path: string;
  label: string;
  seo_title: string | null;
  meta_description: string | null;
  keywords: string | null;
  og_image_url: string | null;
  canonical_url: string | null;
  schema_json: string | null;
  robots: string;
};

type Draft = {
  id?: string;
  path: string;
  label: string;
  seo_title: string;
  meta_description: string;
  keywords: string;
  og_image_url: string;
  canonical_url: string;
  schema_json: string;
  robots: string;
};

const empty: Draft = {
  path: "/",
  label: "",
  seo_title: "",
  meta_description: "",
  keywords: "",
  og_image_url: "",
  canonical_url: "",
  schema_json: "",
  robots: "index,follow",
};

function SeoAdminPage() {
  const list = useServerFn(listSeoPages);
  const save = useServerFn(saveSeoPage);
  const remove = useServerFn(deleteSeoPage);
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [q, setQ] = useState("");

  const { data, isLoading } = useQuery({ queryKey: ["cms-admin", "seo"], queryFn: () => list() });
  const rows = useMemo(() => {
    const all = (data ?? []) as unknown as SeoRow[];
    const term = q.trim().toLowerCase();
    return term ? all.filter((r) => `${r.path} ${r.label}`.toLowerCase().includes(term)) : all;
  }, [data, q]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["cms-admin", "seo"] });
    void qc.invalidateQueries({ queryKey: ["cms"] });
  };

  const saveMutation = useMutation({
    mutationFn: (d: Draft) =>
      save({
        data: {
          ...d,
          seo_title: d.seo_title || null,
          meta_description: d.meta_description || null,
          keywords: d.keywords || null,
          og_image_url: d.og_image_url || null,
          canonical_url: d.canonical_url || null,
          schema_json: d.schema_json || null,
        } as never,
      }),
    onSuccess: () => { toast.success("SEO entry saved"); setDraft(null); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => { toast.success("Entry removed"); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Couldn't delete the entry."),
  });

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="SEO Management"
        description="Per-page titles, descriptions, canonical URLs, robots rules and structured data."
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by path or page name"
          className="max-w-xs"
        />
        <Button size="sm" className="gap-1" onClick={() => setDraft({ ...empty })}>
          <Plus className="h-3.5 w-3.5" aria-hidden /> Add page
        </Button>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading SEO entries…</p> : null}

      <AdminSection number={1} icon={Search} title="Page metadata" description="Applies to the matching public route.">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No SEO entries match.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {rows.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                <div className="min-w-48 flex-1">
                  <p className="text-sm font-medium text-foreground">{r.label || r.path}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {r.path} · {r.seo_title || "No title set"}
                  </p>
                </div>
                <span className="rounded border px-1.5 text-[10px] uppercase text-muted-foreground">
                  {r.robots}
                </span>
                <div className="ml-auto flex gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() =>
                      setDraft({
                        id: r.id,
                        path: r.path,
                        label: r.label,
                        seo_title: r.seo_title ?? "",
                        meta_description: r.meta_description ?? "",
                        keywords: r.keywords ?? "",
                        og_image_url: r.og_image_url ?? "",
                        canonical_url: r.canonical_url ?? "",
                        schema_json: r.schema_json ?? "",
                        robots: r.robots || "index,follow",
                      })
                    }
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="ghost">
                        <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this SEO entry?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This removes "{r.label || r.path}" permanently. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteMutation.mutate(r.id)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </li>
            ))}
          </ul>
        )}
      </AdminSection>

      <Dialog open={draft !== null} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{draft?.id ? "Edit SEO entry" : "Add SEO entry"}</DialogTitle>
          </DialogHeader>
          {draft ? (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="seo-path" className="text-xs">Path</Label>
                <Input id="seo-path" value={draft.path} onChange={(e) => setDraft({ ...draft, path: e.target.value })} placeholder="/meal-plans" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="seo-label" className="text-xs">Page name</Label>
                <Input id="seo-label" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="seo-title" className="text-xs">SEO title</Label>
                <Input id="seo-title" value={draft.seo_title} onChange={(e) => setDraft({ ...draft, seo_title: e.target.value })} />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="seo-desc" className="text-xs">Meta description</Label>
                <Textarea id="seo-desc" rows={2} value={draft.meta_description} onChange={(e) => setDraft({ ...draft, meta_description: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="seo-keys" className="text-xs">Keywords</Label>
                <Input id="seo-keys" value={draft.keywords} onChange={(e) => setDraft({ ...draft, keywords: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="seo-robots" className="text-xs">Robots</Label>
                <Input id="seo-robots" value={draft.robots} onChange={(e) => setDraft({ ...draft, robots: e.target.value })} placeholder="index,follow" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="seo-canon" className="text-xs">Canonical URL</Label>
                <Input id="seo-canon" value={draft.canonical_url} onChange={(e) => setDraft({ ...draft, canonical_url: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Social share image</Label>
                <MediaField value={draft.og_image_url} onChange={(v) => setDraft({ ...draft, og_image_url: v })} allowUrlInput />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="seo-schema" className="text-xs">Structured data (JSON-LD)</Label>
                <Textarea id="seo-schema" rows={5} value={draft.schema_json} onChange={(e) => setDraft({ ...draft, schema_json: e.target.value })} className="font-mono text-xs" />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
            <Button
              disabled={!draft?.path.trim() || !draft?.label.trim() || saveMutation.isPending}
              onClick={() => draft && saveMutation.mutate(draft)}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
